// Oppgave-prioritering for analytikere (Triage-AI, regelbasert). Rangerer åpne
// RFI-er til en smart arbeidskø som veier flere faktorer, ikke bare prioritet:
//   * RFI-prioritet (FLASH→ROUTINE)
//   * frist-hastegrad (forfalt / nær frist teller mest)
//   * koblet etterretningskrav (PIR > CCIR > FFIR > EEFI)
//   * alder (unngår at gamle lav-prioritets-RFI-er sulter i hjel)
// Transparent poengsum + begrunnelse (sporbart — jf. Delta-lærdommen). «AI» her
// = deterministiske regler; en ML-modell kan senere justere vektene.
import { RFI_PRIORITY_RANK } from './rfi.js';
/** Standardvekter — frist og prioritet dominerer, alder hindrer utsulting. */
export const DEFAULT_WEIGHTS = { priority: 40, deadline: 35, requirement: 15, age: 10 };
const REQUIREMENT_WEIGHT = { PIR: 1, CCIR: 0.75, FFIR: 0.5, EEFI: 0.5 };
/** Klam en verdi til [0,1]. */
function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }
/**
 * Poengsett én RFI (0–100). `nowMs` for frist/alder. `reqIndex` slår opp koblet
 * etterretningskrav for å veie PIR tyngre. `horizonMs` = alders-metnings-vindu.
 */
export function scoreRfi(rfi, nowMs, weights = DEFAULT_WEIGHTS, reqIndex, horizonMs = 24 * 3_600_000) {
    const reasons = [];
    // Prioritet: FLASH=1.0 … ROUTINE=0.0 (4 nivåer, rank 0..3).
    const pr = 1 - RFI_PRIORITY_RANK[rfi.priority] / 3;
    if (rfi.priority !== 'routine')
        reasons.push(`prioritet ${rfi.priority}`);
    // Frist: forfalt = 1.0, ellers nærmere frist → høyere. Ingen frist = 0.3 (nøytral).
    let dl = 0.3;
    if (rfi.dueMs !== undefined) {
        const remaining = rfi.dueMs - nowMs;
        if (remaining <= 0) {
            dl = 1;
            reasons.push('FORFALT');
        }
        else {
            dl = clamp01(1 - remaining / horizonMs);
            if (dl > 0.6)
                reasons.push('nær frist');
        }
    }
    // Etterretningskrav.
    let req = 0;
    if (rfi.requirementId && reqIndex) {
        const r = reqIndex.get(rfi.requirementId);
        if (r) {
            req = REQUIREMENT_WEIGHT[r.kind];
            reasons.push(`koblet ${r.kind}`);
        }
    }
    // Alder: eldre RFI stiger sakte (anti-utsulting).
    const age = clamp01((nowMs - rfi.createdMs) / horizonMs);
    const raw = weights.priority * pr + weights.deadline * dl + weights.requirement * req + weights.age * age;
    const max = weights.priority + weights.deadline + weights.requirement + weights.age;
    const score = Math.round((raw / max) * 100);
    return { rfi, score, reasons };
}
/**
 * Bygg en prioritert analytiker-arbeidskø av åpne/taskede RFI-er (høyest poeng
 * først; lik poeng → eldst først for rettferdighet). Besvarte/lukkede utelates.
 */
export function analystQueue(rfis, nowMs, requirements = [], weights = DEFAULT_WEIGHTS) {
    const reqIndex = new Map(requirements.map((r) => [r.id, r]));
    return rfis
        .filter((r) => r.status === 'open' || r.status === 'tasked')
        .map((r) => scoreRfi(r, nowMs, weights, reqIndex))
        .sort((a, b) => b.score - a.score || a.rfi.createdMs - b.rfi.createdMs);
}
//# sourceMappingURL=analystqueue.js.map